import { IconButton } from '@alto-avios/alto-ui';
import { PressEvent } from '@react-aria/interactions';
import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { pushGTMEvent } from '../../utilities';
import { MobileMenuButton } from '../MobileMenuButton';
import { MobileUserDetails } from '../MobileUserDetails';
import { useOverlayContext } from '../Overlay/useOverlayContext';
import { CMSProviderContext } from '../services/content';

import styles from './MobileMenuList.module.css';
import { MenuItem, MenuLeafItem, MenuNodeItem, MobileMenuListProps } from './MobileMenuList.props';

const isLeafItem = (item: MenuItem) => 'url' in item;

export const MobileMenuList: FC<MobileMenuListProps> = ({ isLoggedIn, loginProps, logoutProps }) => {
  const { header } = useContext(CMSProviderContext);
  const overlayContext = useOverlayContext();

  const [navigationStack, setNavigationStack] = useState<MenuItem[]>([header.data.mobileNav] as MenuItem[]);
  const [currentLevel, setCurrentLevel] = useState<number>(1);
  const [navElement, setNavElement] = useState<string>();

  useEffect(() => {
    if (header.data.mobileNav) {
      setNavigationStack([{ ...header.data.mobileNav, children: header.data.mobileNav.children ?? [] }]);
      setCurrentLevel(1);
    }
  }, [header.data.mobileNav]);

  const currentNode = useMemo(() => navigationStack[navigationStack.length - 1] as MenuNodeItem, [navigationStack]);
  const hasIconStart = currentNode.children?.some((item): item is MenuLeafItem => item.startingIcon !== undefined);

  const onLeafPress = (event: PressEvent) => {
    overlayContext?.close();
    handlePushNavigationEventOnLeaf(event);
  };

  const onNodePress = (event: PressEvent, item: MenuNodeItem) => {
    handleNavigateToNode(item);
    handleTrackNavElementOnNode(event);
  };

  const handleNavigateToNode = (node: MenuNodeItem) => {
    setNavigationStack([...navigationStack, node]);
    setCurrentLevel(currentLevel + 1);
  };

  const navigateBack = () => {
    if (navigationStack.length > 1) {
      setNavigationStack(navigationStack.slice(0, -1));
      setCurrentLevel(currentLevel - 1);
    }
  };

  const handleTrackNavElementOnNode = (event: PressEvent) => {
    if (currentLevel === 1 && event.target.textContent) {
      setNavElement(event.target.textContent);
    }
  };

  const handlePushNavigationEventOnLeaf = (event: PressEvent) => {
    const navAction = [
      currentNode.label,
      (currentNode.children || []).findIndex((child) => child.label === event.target.textContent) + 1,
      event.target.textContent,
    ].join(' | ');

    if (currentLevel == 1 && event.target.textContent) {
      pushGTMEvent(event.target.textContent, event.target.textContent, currentLevel.toString());
    } else if (currentLevel > 1 && navElement) {
      pushGTMEvent(navElement, navAction, currentLevel.toString());
    }
  };

  const renderChildItem = (item: MenuItem) =>
    item.label ? (
      <li key={item.label}>
        <MobileMenuButton
          aria-label={item.label}
          hasDivider
          hasIconStartInStack={hasIconStart}
          href={isLeafItem(item) ? item.url : undefined}
          iconStartName={item.startingIcon}
          onPress={(event) => (isLeafItem(item) ? onLeafPress(event) : onNodePress(event, item))}
          size="comfortable"
        >
          {item.label}
        </MobileMenuButton>
      </li>
    ) : null;

  const renderChildren = (item: MenuItem) => (isLeafItem(item) ? null : item.children?.map(renderChildItem));

  return (
    <div className={styles.navigationStackContainer} data-testid="mobile-menu-list">
      {navigationStack.length > 1 && (
        <div className={styles.headerItem}>
          <IconButton
            autoFocus
            data-testid="back-button"
            emphasis="quaternary"
            iconProps={{ iconName: 'chevron-left' }}
            onClick={navigateBack}
            styleVariant="neutral"
            aria-label="back button"
          />
          <span className={styles.headerLabel}>{currentNode.label}</span>
        </div>
      )}
      <ul className={styles.ul} aria-label="mobile-menu">
        {currentNode && renderChildren(currentNode)}
      </ul>
      <div className={styles.flex} />
      {navigationStack.length < 2 && <MobileUserDetails isLoggedIn={isLoggedIn} loginProps={loginProps} logoutProps={logoutProps} showHelpCentre />}
    </div>
  );
};
